import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { topAt, type FormedTemplate } from './shipForm';
import type { ShipTier } from './hullParts';
import { sparSolid } from './shipSolids';
import { timberPost } from './shipTimber';
import {
  BOWSPRIT,
  MAST,
  MAST_PAINTER_WIDTH_SCALE,
  ROPE_PAINTER_WIDTH_SCALE,
  ROPE_RADIUS,
  STAFF,
  STAFF_HEIGHT,
  YARD_PAINTER_WIDTH_SCALE,
  rope,
  type RigStyles,
} from './rigParts';
import type { PennantObstacle } from './pennant';
import {
  TOP,
  TOPMAST,
  bowspritBittFoot,
  foreMast,
  formedBowsprit,
  formedMasts,
  lowerRadiusAt,
  mainMast,
  shroudHeadY,
  topmastRadiusAt,
  type FormedMast,
} from './shipMastPlan';
import { buildShrouds } from './shipShrouds';

export { formedMasts, formedStaffTops } from './shipMastPlan';

/*
 * The standing rig of a formed ship: masts with their tops, topmasts and pennant staffs, the
 * bowsprit, the shrouds (shipShrouds.ts) and the stays. Every tier sets up the forestay to the
 * bowsprit and the main stay to the foot of the fore mast; the mid and near tiers add the main
 * topmast stay to the fore top (woodcut). Lateen masts carry no stay: it would cross their sail.
 */

const STAY = { under: 0.15, lift: 0.3, topmastHead: 0.4 } as const;
const BITT = { size: 0.3, into: 0.2 } as const;

function spar(
  builder: MeshBuilder,
  style: VertexStyle,
  from: Vec3,
  to: Vec3,
  radii: readonly [number, number]
): void {
  sparSolid(builder, style, {
    from,
    to,
    radiusFrom: radii[0],
    radiusTo: radii[1],
    sides: MAST.sides,
    painterWidthScale: MAST_PAINTER_WIDTH_SCALE,
  });
}

function buildMast(builder: MeshBuilder, style: VertexStyle, mast: FormedMast): void {
  const at = (y: number): Vec3 => [mast.x, y, 0];
  spar(builder, style, at(mast.heel), at(mast.lowerHead), [
    lowerRadiusAt(mast, mast.heel),
    lowerRadiusAt(mast, mast.lowerHead),
  ]);
  if (mast.config.topmast) {
    const heel = mast.topLevel - TOPMAST.doubling;
    spar(builder, style, at(heel), at(mast.head), [
      topmastRadiusAt(mast, heel),
      topmastRadiusAt(mast, mast.head),
    ]);
  }
  if (mast.top) {
    sparSolid(builder, style, {
      from: at(mast.top.y - TOP.depth),
      to: at(mast.top.y),
      radiusFrom: TOP.flare * mast.top.radius,
      radiusTo: mast.top.radius,
      sides: TOP.sides,
      painterWidthScale: 1,
    });
  }
  if (mast.config.pennant !== false) {
    const radius = STAFF.radius;
    spar(builder, style, at(mast.head - STAFF.footing), at(mast.head + STAFF_HEIGHT), [
      radius,
      radius,
    ]);
  }
}

function buildBowsprit(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  style: VertexStyle
): void {
  const bowsprit = formedBowsprit(template);
  sparSolid(builder, style, {
    from: bowsprit.heel,
    to: bowsprit.tip,
    radiusFrom: BOWSPRIT.radius,
    radiusTo: BOWSPRIT.radius * BOWSPRIT.taper,
    sides: MAST.sides,
    painterWidthScale: YARD_PAINTER_WIDTH_SCALE,
  });
  if (tier === 'far') return;
  const [x, y] = bowsprit.heel;
  const foot = bowspritBittFoot(template);
  timberPost(builder, style, [x, foot, 0], y - foot + BITT.into, BITT.size);
}

/* A stay leaves its mast from the fore side just under the top and ends inside the next spar. */
function stayHead(mast: FormedMast): Vec3 {
  const y = shroudHeadY(mast) - STAY.under;
  return [mast.x - lowerRadiusAt(mast, y) / 2, y, 0];
}

/** A stay from its head down to its foot, and whether only the mid and near tiers set it up. */
export interface Stay {
  readonly head: Vec3;
  readonly foot: Vec3;
  readonly detail: boolean;
}

export function formedStays(template: FormedTemplate): readonly Stay[] {
  const masts = formedMasts(template);
  const [fore, main] = [foreMast(masts), mainMast(masts)];
  const low = topAt(template, fore.config.position) + STAY.lift;
  const stays: Stay[] = [
    { head: stayHead(fore), foot: formedBowsprit(template).stay, detail: false },
    { head: stayHead(main), foot: [fore.x + lowerRadiusAt(fore, low) / 2, low, 0], detail: false },
  ];
  if (!main.config.topmast) return stays;
  const high = main.head - STAY.topmastHead;
  const top = fore.topLevel + STAY.lift;
  const radius = fore.config.topmast ? topmastRadiusAt(fore, top) : lowerRadiusAt(fore, top);
  const head: Vec3 = [main.x - topmastRadiusAt(main, high) / 2, high, 0];
  return [...stays, { head, foot: [fore.x + radius / 2, top, 0], detail: true }];
}

/**
 * What a pennant must end short of: every mast up to its staff or head, as wide as the painter
 * view draws it, every round top by its rim, and every stay.
 */
export function formedPennantObstacles(template: FormedTemplate): readonly PennantObstacle[] {
  const masts = formedMasts(template);
  const spars = masts.map((mast): PennantObstacle => {
    const top = mast.config.pennant === false ? mast.head : mast.head + STAFF_HEIGHT;
    return {
      from: [mast.x, mast.heel, 0],
      to: [mast.x, top, 0],
      room: MAST.radius * MAST_PAINTER_WIDTH_SCALE,
    };
  });
  const tops = masts.flatMap((mast): PennantObstacle[] =>
    mast.top
      ? [
          {
            from: [mast.x, mast.top.y - TOP.depth, 0],
            to: [mast.x, mast.top.y, 0],
            room: mast.top.radius,
          },
        ]
      : []
  );
  const ropes = formedStays(template).map(({ head, foot }) => ({
    from: head,
    to: foot,
    room: ROPE_RADIUS * ROPE_PAINTER_WIDTH_SCALE,
  }));
  return [...spars, ...tops, ...ropes];
}

function buildStays(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  style: VertexStyle
): void {
  for (const stay of formedStays(template)) {
    if (tier !== 'far' || !stay.detail) rope(builder, style, stay.head, stay.foot);
  }
}

export function buildStandingRig(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  styles: RigStyles
): void {
  for (const mast of formedMasts(template)) buildMast(builder, styles.spar, mast);
  buildBowsprit(builder, template, tier, styles.spar);
  buildShrouds(builder, template, tier, styles);
  buildStays(builder, template, tier, styles.rope);
}
